import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import MicOffIcon from '@mui/icons-material/MicOff';
import SpeakerIcon from '@mui/icons-material/Speaker';
import UsbOffIcon from '@mui/icons-material/UsbOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import * as ETVConstants from '../../ETVConstants';

const POLL_INTERVAL_MS = 30000;

function translated(_key, _fallback) {
  const _value = ETVConstants.trans(_key);
  return _value === `[${_key}]` ? _fallback : _value;
}

function formatDate(_millis) {
  if (!_millis) return '-';
  return new Date(Number(_millis)).toLocaleString();
}

function displayLocation(_location) {
  if (!_location) return '';
  if (typeof _location === 'string') return _location;
  return _location.name || _location.address || _location.label || '';
}

function isEnabled(_value) {
  return _value === true || _value === 1 || _value === '1' || _value === 'true';
}

function delay(_milliseconds) {
  return new Promise((_resolve) => window.setTimeout(_resolve, _milliseconds));
}

async function waitForCommandReply(_base, _parentId, _messageId) {
  if (!_messageId) return;
  for (let _attempt = 0; _attempt < 12; _attempt += 1) {
    const _url = `${_base}/firebase?action=reply_message_status&message_id=${encodeURIComponent(_messageId)}`;
    const _response = await fetch(_url, {
      cache: 'no-store',
      headers: {
        user_id: String(_parentId),
        access_token: localStorage.getItem('etv.access_token') || '',
      },
    });
    if (!_response.ok) throw new Error(`HTTP ${_response.status}`);
    const _body = await _response.json();
    let _reply = _body?.reply_message;
    if (typeof _reply === 'string' && _reply.trim()) {
      try {
        _reply = JSON.parse(_reply);
      } catch (_ignore) {
        _reply = null;
      }
    }
    if (_reply && typeof _reply === 'object') {
      if (_reply.success === false) {
        throw new Error(_reply.reason || 'Device rejected self test');
      }
      return;
    }
    await delay(1000);
  }
  throw new Error('Device did not acknowledge the self-test command');
}

function testState(_status) {
  if (!_status || !_status.last_attempt_at_ms) {
    return {
      label: translated('alarm_test_never_tested', 'Never tested'),
      color: 'warning',
    };
  }
  const _overdue = Number(_status.next_test_due_at_ms || 0) > 0
    && Number(_status.next_test_due_at_ms) < Date.now();
  if (String(_status.last_result || '').toUpperCase() === 'CONFIGURATION_ERROR') {
    return {
      label: translated('alarm_test_configuration_error', 'Configuration error'),
      color: 'error',
    };
  }
  if (_status.fault_active || _overdue) {
    return {
      label: translated('alarm_test_fault', 'Fault'),
      color: 'error',
    };
  }
  if (String(_status.last_result || '').toUpperCase() === 'SUCCESS') {
    return {
      label: translated('alarm_test_ok', 'OK'),
      color: 'success',
    };
  }
  return {
    label: String(_status.last_result || translated('alarm_test_pending', 'Pending'))
      .replaceAll('_', ' '),
    color: 'warning',
  };
}

function testOrder(_status) {
  // Required display order: tested faults first, screens without any test
  // second, and successfully tested screens last.
  if (!_status || !_status.last_attempt_at_ms) return 1;
  return testState(_status).color === 'success' ? 2 : 0;
}

function failureDiagnostics(_status, _device) {
  const _diagnostics = [];
  const _add = (_code, _label, _icon) => {
    if (_diagnostics.some((_diagnostic) => _diagnostic.code === _code)) return;
    _diagnostics.push({ code: _code, label: _label, icon: _icon });
  };
  const _code = String(_status?.failure_code || '').trim().toLowerCase();
  const _configurationCodes = [
    'intercom_app_not_installed',
    'no_emergency_contact_configured',
    'emergency_contact_configuration_unavailable',
    'sip_not_configured',
  ];
  if (_code === 'intercom_app_not_installed') {
    _add(_code, translated('alarm_test_intercom_app_missing', 'Intercom app not installed'), <PhoneDisabledIcon />);
  } else if (_code === 'no_emergency_contact_configured') {
    _add(_code, translated('alarm_test_emergency_contact_missing', 'No emergency contact configured'), <PhoneDisabledIcon />);
  } else if (_code === 'emergency_contact_configuration_unavailable') {
    _add(_code, translated('alarm_test_contact_config_unavailable', 'Emergency contact configuration unavailable'), <WarningAmberIcon />);
  } else if (_code === 'sip_not_configured') {
    _add(_code, translated('alarm_test_sip_not_configured', 'SIP account not configured'), <PhoneDisabledIcon />);
  } else if (_code === 'sip_registration_failed') {
    _add(_code, translated('alarm_test_sip_registration_failed', 'SIP registration failed'), <PhoneDisabledIcon />);
  } else if (_code === 'intercom_service_unavailable') {
    _add(_code, translated('alarm_test_intercom_service_unavailable', 'Intercom service unavailable'), <PhoneDisabledIcon />);
  } else if (_code === 'expected_usb_audio_device_missing') {
    _add(_code, translated('alarm_test_usb_speakerphone_missing', 'USB speakerphone missing'), <UsbOffIcon />);
  } else if (_code === 'usb_audio_pair_incomplete') {
    _add(_code, translated('alarm_test_usb_audio_incomplete', 'USB speakerphone audio incomplete'), <UsbOffIcon />);
  } else if (_code.includes('speaker')) {
    _add(_code, translated('alarm_test_speaker_not_working', 'No working speaker detected'), <SpeakerIcon />);
  } else if (_code.includes('microphone')) {
    _add(_code, translated('alarm_test_microphone_not_working', 'No working microphone detected'), <MicOffIcon />);
  } else if (_code === 'internal_audio_pair_incomplete') {
    _add(_code, translated('alarm_test_internal_audio_missing', 'Speaker or microphone missing'), <WarningAmberIcon />);
  } else if (_code === 'acoustic_tone_not_detected'
      || _code === 'acoustic_path_test_failed') {
    _add(_code, translated('alarm_test_audio_path_not_detected', 'Audio path not detected'), <WarningAmberIcon />);
  } else if (_code.includes('timeout')) {
    _add(_code, translated('alarm_test_call_timeout', 'Test call timed out'), <PhoneDisabledIcon />);
  } else if (_code && !_code.includes('no_users') && !_code.includes('no users')) {
    _add(_code, translated('alarm_test_failure_generic', 'Alarm-path test failed'), <WarningAmberIcon />);
  }

  const _audio = _status?.audio_diagnostics;
  if (_audio && typeof _audio === 'object') {
    const _audioCode = String(_audio.audioFailureCode || '').trim().toLowerCase();
    const _usbAudioMissing = _audioCode === 'expected_usb_audio_device_missing';
    const _usbAudioIncomplete = _audioCode === 'usb_audio_pair_incomplete';
    if (_usbAudioMissing) {
      _add(
        _audioCode,
        translated('alarm_test_usb_speakerphone_missing', 'USB speakerphone missing'),
        <UsbOffIcon />
      );
    } else if (_usbAudioIncomplete) {
      _add(
        _audioCode,
        translated('alarm_test_usb_audio_incomplete', 'USB speakerphone audio incomplete'),
        <UsbOffIcon />
      );
    }
    if (!_usbAudioMissing && !_usbAudioIncomplete
        && ((_audio.microphoneAvailable === false)
        || (_audio.microphonePathPassed === false))) {
      _add(
        'microphone_test_failed',
        translated('alarm_test_microphone_not_working', 'No working microphone detected'),
        <MicOffIcon />
      );
    }
    if (!_usbAudioMissing && !_usbAudioIncomplete
        && ((_audio.speakerAvailable === false)
        || (_audio.speakerPathPassed === false))) {
      _add(
        'speaker_test_failed',
        translated('alarm_test_speaker_not_working', 'No working speaker detected'),
        <SpeakerIcon />
      );
    }
    const _audioTestFailed = _audio.acousticTestPerformed === true
      && _audio.acousticLoopPassed === false;
    if (_audioTestFailed
        && _audio.microphonePathPassed !== false
        && _audio.speakerPathPassed !== false) {
      _add(
        'audio_path_test_failed',
        translated('alarm_test_audio_path_not_detected', 'Audio path not detected'),
        <WarningAmberIcon />
      );
    }
  }

  const _en8128Enabled = isEnabled(_device?.en81_28_alarm_workflow_enabled);
  const _testCallNumber = String(
    _device?.sip_test_extension
      || _device?.sip_test_number
      || _device?.testcall_address
      || ''
  ).trim();
  if (_en8128Enabled && !_testCallNumber) {
    _add(
      'missing_test_call_number',
      translated('alarm_test_number_missing', 'Test-call number missing'),
      <PhoneDisabledIcon />
    );
  }

  const _failed = String(_status?.last_result || '').toUpperCase() === 'FAILED'
    || Boolean(_status?.fault_active);
  const _alarmPathFailed = _failed
    && !_configurationCodes.includes(_code)
    && _code !== 'intercom_service_unavailable'
    && _status?.same_alarm_path !== true;
  if (_alarmPathFailed) {
    const _routeMissing = !String(_status?.last_route || '').trim()
      && !String(_status?.last_destination || '').trim();
    const _contactMissing = _code.includes('no_users') || _code.includes('no users');
    _add(
      _routeMissing || _contactMissing ? 'missing_alarm_path' : 'failed_alarm_path',
      _routeMissing || _contactMissing
        ? translated('alarm_test_alarm_path_missing', 'Alarm path/contact missing')
        : translated('alarm_test_alarm_path_failed', 'Alarm-path test failed'),
      <AltRouteIcon />
    );
  }

  return _diagnostics;
}

export default function IntercomAlarmTestOverview({ parentId, devices = [] }) {
  const [_statuses, _setStatuses] = React.useState([]);
  const [_loading, _setLoading] = React.useState(true);
  const [_error, _setError] = React.useState('');
  const [_executingDeviceId, _setExecutingDeviceId] = React.useState('');
  const [_commandMessage, _setCommandMessage] = React.useState(null);

  const loadStatuses = React.useCallback(async (_showSpinner = false) => {
    if (!parentId) return;
    if (_showSpinner) _setLoading(true);
    try {
      const _base = (ETVConstants.getServerBase?.() || '').replace(/\/$/, '');
      const _url = `${_base}/intercom_alarm_test?action=list_status&parent_id=${encodeURIComponent(parentId)}`;
      const _response = await fetch(_url, {
        cache: 'no-store',
        headers: {
          user_id: String(parentId),
          access_token: localStorage.getItem('etv.access_token') || '',
        },
      });
      if (!_response.ok) throw new Error(`HTTP ${_response.status}`);
      const _body = await _response.json();
      if (_body?.result === false) throw new Error(_body.message || 'Request failed');
      const _loadedStatuses = Array.isArray(_body?.statuses) ? _body.statuses : [];
      _setStatuses(_loadedStatuses);
      _setError('');
      return _loadedStatuses;
    } catch (_loadError) {
      _setError(String(_loadError?.message || _loadError));
      return null;
    } finally {
      _setLoading(false);
    }
  }, [parentId]);

  const executeSelfTest = React.useCallback(async (_device) => {
    const _deviceId = String(_device?.device_id || '');
    if (!_deviceId || !parentId) return;
    const _previousTestId = _statuses.find(
      (_status) => String(_status.device_id) === _deviceId
    )?.last_test_id;
    _setExecutingDeviceId(_deviceId);
    _setCommandMessage(null);
    try {
      const _base = (ETVConstants.getServerBase?.() || '').replace(/\/$/, '');
      const _url = `${_base}/intercom_alarm_test?action=execute_self_test&parent_id=${encodeURIComponent(parentId)}&device_id=${encodeURIComponent(_deviceId)}`;
      const _response = await fetch(_url, {
        cache: 'no-store',
        headers: {
          user_id: String(parentId),
          access_token: localStorage.getItem('etv.access_token') || '',
        },
      });
      if (!_response.ok) throw new Error(`HTTP ${_response.status}`);
      const _body = await _response.json();
      if (_body?.result === false) throw new Error(_body.message || _body.reason || 'Request failed');
      await waitForCommandReply(
        _base,
        parentId,
        _body?.firebase?.message_id
      );
      for (let _attempt = 0; _attempt < 16; _attempt += 1) {
        await delay(_attempt === 0 ? 1000 : 2000);
        const _latestStatuses = await loadStatuses(false);
        const _latestStatus = _latestStatuses?.find(
          (_status) => String(_status.device_id) === _deviceId
        );
        const _newTestReceived = _latestStatus?.last_test_id
          && _latestStatus.last_test_id !== _previousTestId;
        const _testFinished = _newTestReceived
          && String(_latestStatus.last_result || '').toUpperCase() !== 'PENDING';
        if (_testFinished) {
          _setCommandMessage(null);
          break;
        }
      }
    } catch (_requestError) {
      _setCommandMessage({
        severity: 'error',
        text: `${translated('alarm_self_test_request_failed', 'Could not request self test')}: ${String(_requestError?.message || _requestError)}`,
      });
    } finally {
      _setExecutingDeviceId('');
    }
  }, [_statuses, loadStatuses, parentId]);

  React.useEffect(() => {
    loadStatuses(true);
    const _timer = window.setInterval(() => loadStatuses(false), POLL_INTERVAL_MS);
    return () => window.clearInterval(_timer);
  }, [loadStatuses]);

  const _statusByDevice = React.useMemo(() => {
    const _map = new Map();
    _statuses.forEach((_status) => _map.set(String(_status.device_id), _status));
    return _map;
  }, [_statuses]);

  const _rows = React.useMemo(() => {
    const _knownDeviceIds = new Set(devices.map((_device) => String(_device.device_id)));
    const _linkedRows = devices.map((_device) => ({
      device: _device,
      status: _statusByDevice.get(String(_device.device_id)) || null,
    }));
    const _orphanRows = _statuses
      .filter((_status) => !_knownDeviceIds.has(String(_status.device_id)))
      .map((_status) => ({ device: null, status: _status }));
    return [..._linkedRows, ..._orphanRows].sort((_left, _right) => {
      const _orderDifference = testOrder(_left.status) - testOrder(_right.status);
      if (_orderDifference !== 0) return _orderDifference;
      const _leftName = _left.device?.device_name
        || _left.status?.device_name || _left.device?.device_id
        || _left.status?.device_id || '';
      const _rightName = _right.device?.device_name
        || _right.status?.device_name || _right.device?.device_id
        || _right.status?.device_id || '';
      return String(_leftName).localeCompare(String(_rightName));
    });
  }, [_statusByDevice, _statuses, devices]);

  const _attentionRows = React.useMemo(
    () => _rows.filter((_row) => testState(_row.status).color !== 'success'),
    [_rows]
  );
  const _faultCount = _attentionRows.length;

  return (
    <Card sx={{ mb: 3, borderLeft: _faultCount ? 5 : 0, borderColor: _faultCount ? 'warning.main' : 'success.main' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <FactCheckOutlinedIcon color={_faultCount ? 'warning' : 'success'} />
            <Typography variant="h6">
              {translated('alarm_path_supervision', 'Alarm path supervision')}
            </Typography>
            <Chip
              size="small"
              color={_faultCount ? 'warning' : 'success'}
              label={_faultCount
                ? `${_faultCount} ${translated('alarm_test_attention', 'require attention')}`
                : translated('alarm_tests_all_ok', 'All tests OK')}
            />
          </Stack>
          <Button
            size="small"
            startIcon={_loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => loadStatuses(true)}
            disabled={_loading}
          >
            {translated('refresh', 'Refresh')}
          </Button>
        </Stack>

        {_error && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {translated('alarm_test_backend_unavailable', 'Alarm-path test status is temporarily unavailable.')}
          </Alert>
        )}

        {_commandMessage && (
          <Alert
            severity={_commandMessage.severity}
            onClose={() => _setCommandMessage(null)}
            sx={{ mb: 1.5 }}
          >
            {_commandMessage.text}
          </Alert>
        )}

        {!_loading && !_error && _rows.length === 0 && (
          <Typography color="text.secondary">
            {translated('no_linked_screens', 'No linked elevator screens')}
          </Typography>
        )}

        <Stack spacing={1.25}>
          {_rows.map(({ device: _device, status: _status }) => {
            const _deviceId = _device?.device_id || _status?.device_id;
            const _deviceName = _device?.device_name || _status?.device_name || _deviceId;
            const _commissionNr = _status?.commission_nr || _device?.commission_nr || '';
            const _location = displayLocation(_device?.location);
            const _state = testState(_status);
            const _diagnostics = failureDiagnostics(_status, _device);
            const _en8128Enabled = isEnabled(
              _device?.en81_28_alarm_workflow_enabled
            );
            return (
              <Box key={_deviceId} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography fontWeight={700}>
                        {[_location, _deviceName].filter(Boolean).join(' - ')}
                      </Typography>
                      <Chip size="small" color={_state.color} label={_state.label} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {translated('commission_nr', 'Commission number')}: {_commissionNr || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {translated('alarm_test_last_success', 'Last success')}: {formatDate(_status?.last_success_at_ms)}
                      {' · '}{translated('alarm_test_next_due', 'Next due')}: {formatDate(_status?.next_test_due_at_ms)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {translated('alarm_test_route', 'Route')}: {_status?.last_route || '-'}
                      {_status?.last_destination ? ` · ${_status.last_destination}` : ''}
                      {Number(_status?.consecutive_failures || 0) > 0
                        ? ` · ${translated('alarm_test_consecutive_failures', 'Consecutive failures')}: ${_status.consecutive_failures}`
                        : ''}
                    </Typography>
                    {_diagnostics.length > 0 && (
                      <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.75} sx={{ mt: 0.75 }}>
                        {_diagnostics.map((_diagnostic) => (
                          <Tooltip key={_diagnostic.code} title={_diagnostic.code} placement="bottom-start">
                            <Chip
                              size="small"
                              color="error"
                              variant="outlined"
                              icon={_diagnostic.icon}
                              label={_diagnostic.label}
                              sx={{ fontWeight: 600 }}
                            />
                          </Tooltip>
                        ))}
                      </Stack>
                    )}
                  </Box>
                  {_en8128Enabled && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={_executingDeviceId === String(_deviceId)
                        ? <CircularProgress size={16} />
                        : <PlayArrowIcon />}
                      disabled={Boolean(_executingDeviceId)}
                      onClick={() => executeSelfTest(_device)}
                      sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, whiteSpace: 'nowrap' }}
                    >
                      {translated('execute_self_test', 'Execute self test')}
                    </Button>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}
